import { channelHasMarketplaceAck } from '@/lib/validation'
import type { Order, OrderStatus } from '@/lib/vision/schemas'

/** Statuses Vision still treats as pending Amazon acknowledgement. */
export const PENDING_ACK_STATUSES = [
  'RECEIVED',
  'READY_TO_ACKNOWLEDGE',
  'VALIDATION_FAILED',
] as const satisfies readonly OrderStatus[]

export const MAX_BULK_ACK_POS = 50

export type BulkAckDisableReason =
  | 'not_amazon'
  | 'not_pending'
  | 'other_channel'
  | 'batch_full'
  | 'writes_locked'

export function normalizeAckChannel(channel: string | null | undefined): string {
  return (channel ?? '').trim().toLowerCase().replace(/-/g, '_')
}

export function isAmazonAckChannel(channel: string | null | undefined): boolean {
  return channelHasMarketplaceAck(channel)
}

export function isPendingAckStatus(status: string | null | undefined): boolean {
  const normalized = (status ?? '').toUpperCase()
  return (PENDING_ACK_STATUSES as readonly string[]).includes(normalized)
}

export function isBulkAckEligible(order: Pick<Order, 'channel' | 'status'>): boolean {
  return isAmazonAckChannel(order.channel) && isPendingAckStatus(order.status)
}

/**
 * Whether a queue row can join the current bulk-ack batch.
 * Selected rows stay selectable so the operator can deselect them.
 */
export function bulkAckDisableReason(
  order: Pick<Order, 'id' | 'channel' | 'status'>,
  opts: {
    selectedIds: ReadonlySet<string>
    selectedChannel: string | null
    selectedCount: number
    writesAllowed: boolean
  },
): BulkAckDisableReason | null {
  if (opts.selectedIds.has(order.id)) return null
  if (!opts.writesAllowed) return 'writes_locked'
  if (!isAmazonAckChannel(order.channel)) return 'not_amazon'
  if (!isPendingAckStatus(order.status)) return 'not_pending'
  if (
    opts.selectedChannel != null &&
    normalizeAckChannel(order.channel) !== normalizeAckChannel(opts.selectedChannel)
  ) {
    return 'other_channel'
  }
  if (opts.selectedCount >= MAX_BULK_ACK_POS) return 'batch_full'
  return null
}

export function bulkAckDisableLabel(
  reason: BulkAckDisableReason | null,
  selectedChannel: string | null,
): string | undefined {
  switch (reason) {
    case 'writes_locked':
      return 'Viewer — ask an Operator to acknowledge'
    case 'not_amazon':
      return 'Bulk acknowledgement is only for Amazon and Amazon Now'
    case 'not_pending':
      return 'Only pending-acknowledgement POs can be selected'
    case 'other_channel':
      return selectedChannel
        ? `This batch is limited to ${channelFamilyLabel(selectedChannel)} POs`
        : 'A batch can only contain one Amazon channel family'
    case 'batch_full':
      return `A batch can hold up to ${MAX_BULK_ACK_POS} POs`
    default:
      return undefined
  }
}

export function channelFamilyLabel(channel: string): string {
  const normalized = normalizeAckChannel(channel)
  if (normalized === 'amazon_now') return 'Amazon Now'
  if (normalized === 'amazon') return 'Amazon'
  return channel
}

/** Header checkbox state for the visible page. */
export function pageSelectAllState(
  pageOrders: readonly Order[],
  selectedIds: ReadonlySet<string>,
  selectedChannel: string | null,
  selectedCount: number,
  writesAllowed: boolean,
): {
  targets: Order[]
  checked: boolean | 'indeterminate'
  disabled: boolean
  title: string
} {
  const channel =
    selectedChannel ?? pageOrders.find(order => isBulkAckEligible(order))?.channel ?? null

  const targets = channel
    ? pageOrders.filter(order => {
        if (normalizeAckChannel(order.channel) !== normalizeAckChannel(channel)) return false
        if (!isBulkAckEligible(order)) return false
        return (
          selectedIds.has(order.id) ||
          bulkAckDisableReason(order, {
            selectedIds,
            selectedChannel: channel,
            selectedCount,
            writesAllowed,
          }) == null
        )
      })
    : []

  const selectedOnPage = targets.filter(order => selectedIds.has(order.id)).length
  const checked =
    targets.length > 0 && selectedOnPage === targets.length
      ? true
      : selectedOnPage > 0
        ? 'indeterminate'
        : false
  const canAdd = targets.some(order => !selectedIds.has(order.id))
  const disabled = !writesAllowed || targets.length === 0 || (!canAdd && selectedOnPage === 0)

  return {
    targets,
    checked,
    disabled,
    title: channel
      ? `Select every pending ${channelFamilyLabel(channel)} PO on this page`
      : 'Select a pending Amazon or Amazon Now PO first',
  }
}
